'use client';

import { useTransition } from 'react';

import { isRedirectError } from 'next/dist/client/components/redirect-error';

import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { validateSignupFieldEntries } from '@kit/auth/signup-custom-fields';
import { Button } from '@kit/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@kit/ui/form';
import { Input } from '@kit/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@kit/ui/select';
import { Switch } from '@kit/ui/switch';
import { Trans } from '@kit/ui/trans';

import {
  type ProfileSetupData,
  ProfileSetupSchema,
  type SignupCustomField,
  type SignupFamilyMember,
} from '../_lib/schema/profile-setup.schema';
import { saveProfileSetupAction } from '../_lib/server/profile-setup-actions';

export interface ProfileSetupFormProps {
  parentFields: SignupCustomField[];
  childFields: SignupCustomField[];
  familyMembers: SignupFamilyMember[];
  defaultValues: ProfileSetupData;
  onComplete?: () => void;
}

export function ProfileSetupForm({
  parentFields,
  childFields,
  familyMembers,
  defaultValues,
  onComplete,
}: ProfileSetupFormProps) {
  const [isPending, startTransition] = useTransition();

  const form = useForm<ProfileSetupData>({
    resolver: zodResolver(ProfileSetupSchema),
    defaultValues: {
      ...defaultValues,
      parentFields: defaultValues.parentFields.map((entry) => ({
        ...entry,
        value:
          parentFields.find((field) => field.id === entry.fieldId)
            ?.field_type === 'boolean' && !('boolean' in entry.value)
            ? { boolean: false }
            : entry.value,
      })),
      childFieldGroups: defaultValues.childFieldGroups.map((group) => ({
        ...group,
        fields: group.fields.map((entry) => ({
          ...entry,
          value:
            childFields.find((field) => field.id === entry.fieldId)
              ?.field_type === 'boolean' && !('boolean' in entry.value)
              ? { boolean: false }
              : entry.value,
        })),
      })),
    },
  });

  const onSubmit = (data: ProfileSetupData) => {
    const errors = [
      ...validateSignupFieldEntries(parentFields, data.parentFields, 'parent'),
      ...data.childFieldGroups.flatMap((group) =>
        validateSignupFieldEntries(childFields, group.fields, 'child'),
      ),
    ];
    if (errors.length) {
      form.setError('root', { message: errors[0]!.message });
      return;
    }
    startTransition(async () => {
      try {
        const result = await saveProfileSetupAction({
          ...data,
          ...(onComplete ? { redirectAfterSave: false } : {}),
        });
        if (result?.success) onComplete?.();
      } catch (error) {
        // redirect() internally throws — let it propagate so Next.js handles it
        if (isRedirectError(error)) throw error;
        toast.error('Failed to save. Please try again.');
      }
    });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {/* Parent fields */}
        {parentFields.length > 0 && (
          <div className="space-y-4">
            {familyMembers.length > 0 && (
              <h3 className="text-sm font-medium">
                <Trans i18nKey="auth:yourDetails" defaults="Your Details" />
              </h3>
            )}

            {parentFields.map((field, index) => (
              <FormField
                key={field.id}
                control={form.control}
                name={`parentFields.${index}.value`}
                render={({ field: input }) => (
                  <DynamicField
                    field={field}
                    value={input.value}
                    onValueChange={input.onChange}
                    disabled={isPending}
                  />
                )}
              />
            ))}
          </div>
        )}

        {/* Per-child fields */}
        {childFields.length > 0 && familyMembers.length > 0 && (
          <div className="space-y-6">
            {familyMembers.map((child, childIdx) => (
              <div key={child.child_account_id} className="space-y-4">
                <h3 className="text-sm font-medium">{child.child_name}</h3>

                {childFields.map((field, fieldIdx) => (
                  <FormField
                    key={field.id}
                    control={form.control}
                    name={`childFieldGroups.${childIdx}.fields.${fieldIdx}.value`}
                    render={({ field: input }) => (
                      <DynamicField
                        field={field}
                        value={input.value}
                        onValueChange={input.onChange}
                        disabled={isPending}
                      />
                    )}
                  />
                ))}
              </div>
            ))}
          </div>
        )}

        {form.formState.errors.root && (
          <p role="alert" className="text-destructive text-sm">
            {form.formState.errors.root.message}
          </p>
        )}
        <div className="flex flex-col gap-3">
          <Button
            type="submit"
            className="w-full"
            disabled={isPending}
            data-test="profile-setup-submit"
          >
            {isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                <Trans i18nKey="common:saving" defaults="Saving..." />
              </>
            ) : (
              <Trans
                i18nKey="auth:completeProfile"
                defaults="Complete Profile"
              />
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
}

function DynamicField({
  field,
  onValueChange,
  value,
  disabled,
}: {
  field: SignupCustomField;
  value: ProfileSetupData['parentFields'][number]['value'];
  onValueChange: (
    value: ProfileSetupData['parentFields'][number]['value'],
  ) => void;
  disabled: boolean;
}) {
  switch (field.field_type) {
    case 'text':
      return (
        <FormItem>
          <FormLabel>
            {field.label}
            {field.is_required && ' *'}
          </FormLabel>
          {field.description && (
            <FormDescription>{field.description}</FormDescription>
          )}
          <FormControl>
            <Input
              disabled={disabled}
              value={'text' in value ? value.text : ''}
              onChange={(e) => onValueChange({ text: e.target.value })}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      );

    case 'number':
      return (
        <FormItem>
          <FormLabel>
            {field.label}
            {field.is_required && ' *'}
          </FormLabel>
          {field.description && (
            <FormDescription>{field.description}</FormDescription>
          )}
          <FormControl>
            <Input
              type="number"
              value={'number' in value ? value.number : ''}
              disabled={disabled}
              onChange={(e) => {
                onValueChange(
                  isNaN(e.target.valueAsNumber)
                    ? { text: '' }
                    : { number: e.target.valueAsNumber },
                );
              }}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      );

    case 'date':
      return (
        <FormItem>
          <FormLabel>
            {field.label}
            {field.is_required && ' *'}
          </FormLabel>
          {field.description && (
            <FormDescription>{field.description}</FormDescription>
          )}
          <FormControl>
            <Input
              type="date"
              className="min-w-0 max-w-full"
              value={'date' in value ? value.date : ''}
              disabled={disabled}
              onChange={(e) => onValueChange({ date: e.target.value })}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      );

    case 'select':
      return (
        <FormItem>
          <FormLabel>
            {field.label}
            {field.is_required && ' *'}
          </FormLabel>
          {field.description && (
            <FormDescription>{field.description}</FormDescription>
          )}
          <Select
            value={'select' in value ? value.select : ''}
            disabled={disabled}
            onValueChange={(value) => onValueChange({ select: value })}
          >
            <FormControl>
              <SelectTrigger>
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {(field.options ?? []).map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      );

    case 'boolean':
      return (
        <FormItem className="flex items-center justify-between rounded-lg border p-4">
          <div className="space-y-0.5">
            <FormLabel className="cursor-pointer">
              {field.label}
              {field.is_required && ' *'}
            </FormLabel>
            {field.description && (
              <FormDescription className="text-xs">
                {field.description}
              </FormDescription>
            )}
          </div>
          <FormControl>
            <Switch
              checked={'boolean' in value ? value.boolean : false}
              disabled={disabled}
              onCheckedChange={(checked) => onValueChange({ boolean: checked })}
            />
          </FormControl>
        </FormItem>
      );

    default:
      return null;
  }
}
